import { useCallback, useEffect } from 'react'
import { type Moment } from 'moment'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import {
  type GetInsightsPortalGroupComparisonQueryVariables,
  InsightsPortalOwner,
  InsightsPortalSchema,
  useGetInsightsPortalFilterOptionsLazyQuery,
  useGetInsightsPortalGroupComparisonQuery,
  useGetInsightsPortalMetricsQuery,
} from 'domains/insights-portal/api'
import { useInsightsPortalAuthorizationContext } from 'domains/insights-portal/utils/authorization-context.hooks'
import { useFilterParamsContext } from 'domains/insights-portal/utils/filter-params/filterParamsContext'
import { optionsArrayToInFilterSelection } from 'domains/insights-portal/schemas/utils/insightsPortalFilterTransforms'
import {
  createFilterParamConfig,
  transformApiOptionsIntoDashboardOptions,
  transformFilterParamsToApiFilterSelectionsFactory,
} from 'domains/insights-portal/schemas/utils/insightsPortalSchemas.utils'
import { type FilterParamsState } from 'domains/insights-portal/utils/filter-params/filterParams.types'
import { SearchTermMetrics } from 'domains/insights-portal/schemas/search-term/insightsSearchTerm.types'
import { useFormatValue } from 'domains/insights-portal/utils/format-value.hooks'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import { calculateComparisonPeriod } from 'domains/insights-portal/utils/insights-portal.utils'
import { InsightsPortalGroupComparisonRequestJoinBy } from 'domains/insights-portal/api'
import { type SearchTermByPartnerMetrics } from 'domains/insights-portal/schemas/search-term-by-partner-benchmark/insightsSearchTermByPartnerBenchmark.types'
import { type SearchTermByTermMetrics } from 'domains/insights-portal/schemas/search-term-by-term-benchmark/insightsSearchTermByTermBenchmark.types'
import { useInsightsPortalQueryTags } from 'domains/insights-portal/utils/query-tags.hooks'
import { useSchema } from 'domains/insights-portal/utils/schema.hooks'

const transformFilterParamsForApiConfig = [
  createFilterParamConfig('retailer', 'WAREHOUSE', optionsArrayToInFilterSelection('stringValue')),
  createFilterParamConfig('state', 'ZONE_STATE', optionsArrayToInFilterSelection('stringValue')),
  createFilterParamConfig('region', 'REGION', optionsArrayToInFilterSelection('stringValue')),
  createFilterParamConfig(
    'orderSource',
    'ORDER_SOURCE',
    optionsArrayToInFilterSelection('stringValue')
  ),
  createFilterParamConfig(
    'popularSearchTerm',
    'POPULAR_SEARCH_TERM',
    optionsArrayToInFilterSelection('stringValue')
  ),
]

export const searchTermSchemaFilterParamsConfig = transformFilterParamsForApiConfig

export const mapApiNameToDashboardName = {
  WAREHOUSE: 'retailer',
  ZONE_STATE: 'state',
  REGION: 'region',
  ORDER_SOURCE: 'orderSource',
  POPULAR_SEARCH_TERM: 'popularSearchTerm',
}

export const transformSearchApiOptionsConfig = {
  WAREHOUSE: 'stringValue',
  ZONE_STATE: 'stringValue',
  REGION: 'stringValue',
  ORDER_SOURCE: 'stringValue',
  POPULAR_SEARCH_TERM: 'stringValue',
} as const

export const transformFilterSelectionsToSearchSchemaFilters =
  transformFilterParamsToApiFilterSelectionsFactory(transformFilterParamsForApiConfig)

export const useSearchTermFilterOptions = () => {
  const [getFilters, { data, loading, error }] = useGetInsightsPortalFilterOptionsLazyQuery()

  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const schema = useSchema(InsightsPortalSchema.SearchTerm)

  const queryTags = useInsightsPortalQueryTags()

  useEffect(() => {
    getFilters({
      variables: {
        schema,
        partnerIds,
        retailerIds,
        retailerLocationIds,
        filters: transformFilterSelectionsToSearchSchemaFilters(selectedFiltersValues, dates),
        owner: InsightsPortalOwner.RetailerTools,
        queryTags,
      },
    })
  }, [
    selectedFiltersValues,
    getFilters,
    dates,
    partnerIds,
    retailerIds,
    retailerLocationIds,
    schema,
    queryTags,
  ])

  const filterOptions = transformApiOptionsIntoDashboardOptions(
    data?.insightsPortalServiceGetInsightsPortalFilterOptions?.filterOptions,
    transformSearchApiOptionsConfig
  )
  return {
    filterOptions,
    loading,
    error,
  }
}

const useSearchTermMetrics = (
  selectedFiltersValues: FilterParamsState['selectedFiltersValues'],
  dates: { from: Moment; to: Moment },
  metrics: SearchTermMetrics[]
) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')
  const currentPeriodDateRange = {
    from,
    to,
  }

  const { data, loading } = useGetInsightsPortalMetricsQuery({
    variables: {
      schema: useSchema(InsightsPortalSchema.SearchTerm),
      partnerIds,
      retailerIds,
      retailerLocationIds,
      metrics,
      filters: transformFilterSelectionsToSearchSchemaFilters(
        selectedFiltersValues,
        currentPeriodDateRange
      ),
    },
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalMetricValues?.metricValues || [],
    loading,
  }
}

export const useCurrentPeriodSearchTermMetrics = (metrics: SearchTermMetrics[]) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  return useSearchTermMetrics(selectedFiltersValues, currentPeriodDateRange, metrics)
}

export const useLastPeriodSearchTermMetrics = (metrics: SearchTermMetrics[]) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const lastPeriodDateRange = calculateComparisonPeriod(
    from,
    to,
    selectedFiltersValues.comparisonType
  )

  return useSearchTermMetrics(selectedFiltersValues, lastPeriodDateRange, metrics)
}

export const useSearchTermMetricValueFormatter = () => {
  const { formatInteger, formatPercentage } = useFormatValue()

  const formatter = useCallback(
    (metric: SearchTermMetrics, value: number) => {
      const formatters = [
        { metric: SearchTermMetrics.COUNT_SEARCH, formatter: formatInteger, compact: false },
        {
          metric: SearchTermMetrics.SEARCH_CONVERSION,
          formatter: formatPercentage,
          compact: false,
        },
        { metric: SearchTermMetrics.COUNT_NULL_SEARCH, formatter: formatInteger, compact: false },
      ]

      const metricFormatter = formatters.find(f => f.metric === metric)

      if (metricFormatter) {
        return metricFormatter.formatter(value, metricFormatter.compact)
      }
      return value.toLocaleString()
    },
    [formatInteger, formatPercentage]
  )

  return {
    formatter,
  }
}

export const useSearchTermMetricLabels = () =>
  useDomainMessages({
    COUNT_SEARCH: 'insightsPortalDomain.searchDashboard.metrics.countSearch',
    SEARCH_CONVERSION: 'insightsPortalDomain.searchDashboard.metrics.searchConversion',
    COUNT_NULL_SEARCH: 'insightsPortalDomain.searchDashboard.metrics.countNullSearch',
    CONVERSION_GAP: 'insightsPortalDomain.searchDashboard.metrics.conversionGap',
  })

type UseCompareGroupedPeriodsForBenchmarkSearchTerms = {
  metric: SearchTermByPartnerMetrics
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  groupBy: string
  skip?: boolean
  benchmarkSchema:
    | InsightsPortalSchema.SearchTermByPartnerBenchmark
    | InsightsPortalSchema.SearchTermByTermBenchmark
  primarySecondaryMetrics?: SearchTermByPartnerMetrics[]
  comparisonSecondaryMetrics?: Array<SearchTermByPartnerMetrics | SearchTermByTermMetrics>
} & Pick<
  GetInsightsPortalGroupComparisonQueryVariables,
  'orderBy' | 'orderDirection' | 'limit' | 'offset' | 'joinBy'
>

export const useCompareGroupedPeriodsForBenchmarkSearchTerms = ({
  metric,
  groupBy,
  orderBy,
  benchmarkSchema,
  orderDirection,
  limit,
  offset,
  joinBy,
  filtersObjOverride = null,
  skip = false,
  primarySecondaryMetrics,
  comparisonSecondaryMetrics,
}: UseCompareGroupedPeriodsForBenchmarkSearchTerms) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()
  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const lastPeriodDateRange = calculateComparisonPeriod(
    from,
    to,
    selectedFiltersValues.comparisonType
  )

  const currentPeriodFilters = transformFilterSelectionsToSearchSchemaFilters(
    filterValues,
    currentPeriodDateRange
  )

  const lastPeriodFilters = transformFilterSelectionsToSearchSchemaFilters(
    filterValues,
    lastPeriodDateRange
  )

  const primaryDatasetInput = {
    schema: useSchema(benchmarkSchema),
    filters: currentPeriodFilters,
    metric,
    groupBy,
    secondaryMetrics: primarySecondaryMetrics,
  }
  const comparisonDatasetInput = {
    schema: useSchema(benchmarkSchema),
    filters: lastPeriodFilters,
    metric: metric,
    groupBy,
    secondaryMetrics: comparisonSecondaryMetrics,
  }

  const variables: GetInsightsPortalGroupComparisonQueryVariables = {
    partnerIds,
    retailerIds,
    retailerLocationIds,
    primaryDataset: primaryDatasetInput,
    comparisonDataset: comparisonDatasetInput,
    joinBy: joinBy || InsightsPortalGroupComparisonRequestJoinBy.RowNumber,
    orderBy,
    orderDirection,
    limit,
    offset,
  }

  const { data, loading, error } = useGetInsightsPortalGroupComparisonQuery({
    variables,
    skip: skip,
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupComparison?.values || [],
    loading,
    error,
  }
}

type UseCompareGroupedPeriodsForSearchTerms = {
  metric: SearchTermMetrics
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  groupBy: string
  skip?: boolean
  primarySecondaryMetrics?: SearchTermMetrics[]
  comparisonSecondaryMetrics?: Array<SearchTermByPartnerMetrics | SearchTermByTermMetrics>
} & Pick<
  GetInsightsPortalGroupComparisonQueryVariables,
  'orderBy' | 'orderDirection' | 'limit' | 'offset' | 'joinBy'
>

export const useCompareGroupedPeriodsForSearchTerms = ({
  metric,
  groupBy,
  orderBy,
  orderDirection,
  limit,
  offset,
  joinBy,
  filtersObjOverride = null,
  skip = false,
  primarySecondaryMetrics,
  comparisonSecondaryMetrics,
}: UseCompareGroupedPeriodsForSearchTerms) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()
  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const lastPeriodDateRange = calculateComparisonPeriod(
    from,
    to,
    selectedFiltersValues.comparisonType
  )

  const currentPeriodFilters = transformFilterSelectionsToSearchSchemaFilters(
    filterValues,
    currentPeriodDateRange
  )

  const lastPeriodFilters = transformFilterSelectionsToSearchSchemaFilters(
    filterValues,
    lastPeriodDateRange
  )

  const primaryDatasetInput = {
    schema: useSchema(InsightsPortalSchema.SearchTerm),
    filters: currentPeriodFilters,
    metric,
    groupBy,
    secondaryMetrics: primarySecondaryMetrics,
  }
  const comparisonDatasetInput = {
    schema: useSchema(InsightsPortalSchema.SearchTerm),
    filters: lastPeriodFilters,
    metric: metric,
    groupBy,
    secondaryMetrics: comparisonSecondaryMetrics,
  }

  const variables: GetInsightsPortalGroupComparisonQueryVariables = {
    partnerIds,
    retailerIds,
    retailerLocationIds,
    primaryDataset: primaryDatasetInput,
    comparisonDataset: comparisonDatasetInput,
    joinBy: joinBy || InsightsPortalGroupComparisonRequestJoinBy.RowNumber,
    orderBy,
    orderDirection,
    limit,
    offset,
  }

  const { data, loading, error } = useGetInsightsPortalGroupComparisonQuery({
    variables,
    skip: skip,
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupComparison?.values || [],
    loading,
    error,
  }
}

type UseCompareToBenchmarkForSearchTermsArg = {
  metric: SearchTermMetrics
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  groupBy: string
  benchmarkSchema:
    | InsightsPortalSchema.SearchTermByPartnerBenchmark
    | InsightsPortalSchema.SearchTermByTermBenchmark
  skip?: boolean
  comparisonMetric?: SearchTermByPartnerMetrics | SearchTermByTermMetrics
  primarySecondaryMetrics?: SearchTermMetrics[]
  comparisonSecondaryMetrics?: Array<SearchTermByPartnerMetrics | SearchTermByTermMetrics>
} & Pick<
  GetInsightsPortalGroupComparisonQueryVariables,
  'orderBy' | 'orderDirection' | 'limit' | 'offset' | 'joinBy'
>
export const useCompareToBenchmarkForSearchTerms = ({
  metric,
  groupBy,
  orderBy,
  orderDirection,
  limit,
  offset,
  joinBy,
  filtersObjOverride = null,
  skip = false,
  benchmarkSchema,
  comparisonMetric,
  primarySecondaryMetrics,
  comparisonSecondaryMetrics,
}: UseCompareToBenchmarkForSearchTermsArg) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()
  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const filters = transformFilterSelectionsToSearchSchemaFilters(
    filterValues,
    currentPeriodDateRange
  )

  const primaryDatasetInput = {
    schema: useSchema(InsightsPortalSchema.SearchTerm),
    filters: filters,
    metric,
    groupBy,
    secondaryMetrics: primarySecondaryMetrics,
  }

  const comparisonDatasetInput = {
    schema: useSchema(benchmarkSchema),
    filters: filters,
    metric: comparisonMetric || metric,
    groupBy,
    secondaryMetrics: comparisonSecondaryMetrics,
  }

  const variables: GetInsightsPortalGroupComparisonQueryVariables = {
    partnerIds,
    retailerIds,
    retailerLocationIds,
    primaryDataset: primaryDatasetInput,
    comparisonDataset: comparisonDatasetInput,
    joinBy: joinBy || InsightsPortalGroupComparisonRequestJoinBy.GroupValue,
    orderBy,
    orderDirection,
    limit,
    offset,
  }

  const { data, ...rest } = useGetInsightsPortalGroupComparisonQuery({
    variables,
    skip: skip,
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupComparison?.values || [],
    ...rest,
  }
}
